/**
 * ChangeReviewQueue — Task 25.5 (DEC-116). The Paelode change-review section of the
 * Catalog Sync page. Reuses the Task 24.9 change-queue endpoints: lists state-agency
 * diffs, opens a detail panel with old→new values + affected haulers, and lets a
 * PLATFORM_ADMIN Approve / Reject / Defer (approve notifies affected haulers).
 *
 * Ported from web-dispatch/routes/PlatformChangesPage.tsx (web-admin cannot import
 * cross-package) with a state-agency + change-type filter added.
 */
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Pill, subText, fieldStyle } from '../../ui/bits';
import {
  useChangeQueue,
  useChangeDetail,
  useChangeAction,
  useBulkApproveChanges,
  type PlatformChangeDetail,
  type PlatformReviewStatus,
  type AdminAction,
} from './hooks';
import { linkLabel } from './link-label';
import {
  bulkExclusion,
  matchesHaulerFilter,
  planBulk,
  reasonsInWords,
  resultInWords,
  usageLabel,
  type BulkPlan,
  type HaulerFilter,
} from './bulk-approve';
import { formatNumber } from '@lodex-pro/shared/number-format';

const STATUS_TABS: ReadonlyArray<{ key: PlatformReviewStatus | 'ALL'; label: string }> = [
  { key: 'PENDING', label: 'Pending' },
  { key: 'APPROVED', label: 'Approved' },
  { key: 'REJECTED', label: 'Rejected' },
  { key: 'DEFERRED', label: 'Deferred' },
  { key: 'ALL', label: 'All' },
];

const CHANGE_TYPES = [
  'OPERATOR_ID_CHANGED',
  'WELL_STATUS_CHANGED',
  'NEW_OPERATOR',
  'WELL_DEACTIVATED',
  'OPERATOR_DEACTIVATED',
] as const;

const AGENCIES = ['OCC', 'RRC', 'WOGCC', 'NMOCD', 'NDIC'] as const;

/** A date row's ISO value in the viewer's local time (DEC-004); text rows as sent. */
function cell(value: string | null, kind: 'text' | 'date'): string {
  if (value === null) return '—';
  if (kind === 'date') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
  }
  return value;
}

/** The change as labelled old → new rows (built by the API; no ids, no raw JSON). */
function ChangeRows({ change }: { change: PlatformChangeDetail }): React.JSX.Element {
  const rows = change.display.rows;
  return (
    <div style={{ marginTop: '12px', fontSize: '13px' }}>
      {rows.length === 0 ? (
        <div style={subText}>No field values were recorded for this change.</div>
      ) : (
        <table style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: 'var(--color-text-tertiary)', textAlign: 'left' }}>
              <th style={{ fontWeight: 400, paddingRight: '16px' }}>Field</th>
              <th style={{ fontWeight: 400, paddingRight: '16px' }}>Old</th>
              <th style={{ fontWeight: 400, paddingRight: '16px' }} aria-label="changes to" />
              <th style={{ fontWeight: 400 }}>New</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <td style={{ paddingRight: '16px', color: 'var(--color-text-secondary)' }}>
                  {r.label}
                </td>
                <td style={{ paddingRight: '16px' }}>{cell(r.old, r.kind)}</td>
                <td style={{ paddingRight: '16px' }}>→</td>
                <td style={{ fontWeight: 600 }}>{cell(r.new, r.kind)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <details style={{ marginTop: '8px' }}>
        <summary style={{ ...subText, cursor: 'pointer', textTransform: 'none' }}>
          Show raw data
        </summary>
        <pre style={{ margin: '4px 0 0', fontSize: '12px' }}>
          {JSON.stringify({ old: change.oldValues, new: change.newValues }, null, 1)}
        </pre>
      </details>
    </div>
  );
}

/**
 * A detail that throws while rendering shows a line in its place; the queue stays up
 * (Corbin, 2026-10-30: one row blanked the whole page). Keyed by change id, so opening
 * another row starts clean.
 */
class DetailBoundary extends React.Component<
  { onClose: () => void; children: React.ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: unknown): void {
    console.error('Change review detail failed to render', error);
  }

  override render(): React.ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          backgroundColor: 'var(--color-bg-tertiary)',
          border: '1px solid var(--color-border-secondary)',
          borderRadius: '10px',
          padding: '16px',
          marginTop: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '13px',
        }}
      >
        <span>Couldn&apos;t show this change.</span>
        <button
          type="button"
          onClick={this.props.onClose}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </div>
    );
  }
}

function DetailPanel({ id, onClose }: { id: string; onClose: () => void }): React.JSX.Element {
  const { data, isLoading } = useChangeDetail(id);
  const action = useChangeAction();
  const [notes, setNotes] = React.useState('');

  const run = (act: AdminAction): void => {
    action.mutate(
      { id, action: act, notes },
      {
        onSuccess: () => {
          toast.success(
            act === 'approve'
              ? 'Change approved — haulers notified'
              : act === 'reject'
                ? 'Change rejected'
                : 'Deferred',
          );
          onClose();
        },
        onError: () => toast.error('Action failed — try again'),
      },
    );
  };

  const change = data?.change;
  const canAct =
    change !== undefined &&
    (change.reviewStatus === 'PENDING' || change.reviewStatus === 'DEFERRED');

  return (
    <div
      style={{
        backgroundColor: 'var(--color-bg-tertiary)',
        border: '1px solid var(--color-border-secondary)',
        borderRadius: '10px',
        padding: '16px',
        marginTop: '8px',
      }}
    >
      {isLoading || data === undefined || change === undefined ? (
        <p style={subText}>Loading…</p>
      ) : (
        <>
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
          >
            <div>
              <div style={{ fontWeight: 600 }}>
                {change.wellName ?? change.display.summary}
                {change.apiNumber !== null ? (
                  <span style={subText}> · {change.apiNumber}</span>
                ) : null}
              </div>
              <div style={{ ...subText, marginTop: '4px', textTransform: 'none' }}>
                {change.stateAgency} · {change.changeType.replace(/_/g, ' ').toLowerCase()}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-text-secondary)',
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>

          <ChangeRows change={change} />

          <div style={{ marginTop: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>
              Affected haulers ({data.affectedHaulers.length})
            </div>
            {data.affectedHaulers.length === 0 ? (
              <div style={{ ...subText, textTransform: 'none' }}>
                None — no hauler uses this well (no tank battery, pickup or disposal link).
              </div>
            ) : (
              <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px' }}>
                {data.affectedHaulers.map((h) => (
                  <li key={h.tenantId}>
                    {h.tenantName} — {linkLabel(h)}
                    {h.reviewStatus !== null ? ` · ${h.reviewStatus}` : ''}
                  </li>
                ))}
              </ul>
            )}
            {change.operatorCount > 0 || change.openJobCount > 0 ? (
              <div style={{ ...subText, marginTop: '8px', textTransform: 'none' }}>
                Also in use:{' '}
                {usageLabel({ ...change, affectedHaulerCount: data.affectedHaulers.length })}. Bulk
                approve skips this change.
              </div>
            ) : null}
          </div>

          {canAct ? (
            <>
              <textarea
                value={notes}
                onChange={(e): void => setNotes(e.target.value)}
                placeholder="Review notes (optional)"
                rows={2}
                style={{ ...fieldStyle, width: '100%', marginTop: '12px', resize: 'vertical' }}
              />
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <Button
                  type="button"
                  disabled={action.isPending}
                  onClick={(): void => run('approve')}
                  style={{
                    backgroundColor: 'var(--color-accent-blue)',
                    color: 'var(--color-on-accent)',
                  }}
                >
                  Approve
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={action.isPending}
                  onClick={(): void => run('reject')}
                >
                  Reject
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={action.isPending}
                  onClick={(): void => run('defer')}
                >
                  Defer
                </Button>
              </div>
            </>
          ) : (
            <div style={{ ...subText, marginTop: '12px', textTransform: 'none' }}>
              Already {change.reviewStatus.toLowerCase()}.
            </div>
          )}
        </>
      )}
    </div>
  );
}

const HAULER_FILTERS: ReadonlyArray<{ key: HaulerFilter; label: string }> = [
  { key: 'any', label: 'Any haulers' },
  { key: 'none', label: '0 haulers' },
  { key: 'some', label: '1+ haulers' },
];

/** The confirm dialog: how many, under which filters, and what is left out. */
function BulkApproveModal({
  plan,
  filters,
  onOpenChange,
  onDone,
}: {
  plan: BulkPlan | null;
  filters: string;
  onOpenChange: (open: boolean) => void;
  onDone: (summary: string) => void;
}): React.JSX.Element {
  const bulk = useBulkApproveChanges();
  const n = plan?.ids.length ?? 0;

  const confirm = (): void => {
    if (plan === null) return;
    bulk.mutate(
      { ids: plan.ids },
      {
        onSuccess: (r) => {
          const summary = resultInWords(r);
          toast.success(summary);
          onDone(summary);
          onOpenChange(false);
        },
        onError: () => toast.error('Bulk approve failed — nothing more was approved. Try again.'),
      },
    );
  };

  return (
    <Dialog open={plan !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Approve {n} change{n === 1 ? '' : 's'} with 0 haulers?
          </DialogTitle>
        </DialogHeader>
        <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
          Showing: {filters}. Each well change is applied as if you approved it on its own. No
          hauler, open job or operator uses these wells, so nobody is notified.
        </p>
        {plan !== null && plan.operatorDeactivations > 0 ? (
          <p style={{ ...subText, textTransform: 'none' }}>
            {plan.operatorDeactivations} operator deactivation
            {plan.operatorDeactivations === 1 ? ' is' : 's are'} not included: they reach the
            operator&apos;s haulers, so review them one by one.
          </p>
        ) : null}
        {plan !== null && plan.excluded.size > 0 ? (
          <p style={{ ...subText, textTransform: 'none' }}>
            Not included: {reasonsInWords(plan.excluded)}.
          </p>
        ) : null}
        <p style={{ ...subText, textTransform: 'none' }}>
          Each change is checked again as it is approved; any that has gained a hauler, an open job
          or an operator since this list loaded is skipped.
        </p>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={(): void => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={bulk.isPending || n === 0}
            onClick={confirm}
            style={{ backgroundColor: 'var(--color-accent-blue)', color: 'var(--color-on-accent)' }}
          >
            {bulk.isPending ? 'Approving…' : `Approve ${n.toString()}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ChangeReviewQueue(): React.JSX.Element {
  const [tab, setTab] = React.useState<PlatformReviewStatus | 'ALL'>('PENDING');
  const [agency, setAgency] = React.useState('');
  const [changeType, setChangeType] = React.useState('');
  const [haulers, setHaulers] = React.useState<HaulerFilter>('any');
  const [openId, setOpenId] = React.useState<string | null>(null);
  const [bulkPlan, setBulkPlan] = React.useState<BulkPlan | null>(null);
  const [bulkResult, setBulkResult] = React.useState<string | null>(null);
  const { data, isLoading } = useChangeQueue(
    tab === 'ALL' ? undefined : tab,
    agency === '' ? undefined : agency,
    changeType === '' ? undefined : changeType,
  );
  const changes = (data?.changes ?? []).filter((c) => matchesHaulerFilter(c, haulers));
  const pendingCount = tab === 'PENDING' ? changes.length : undefined;
  const plan = planBulk(changes);
  const filtersInWords = [
    agency === '' ? 'all agencies' : agency,
    changeType === '' ? 'all change types' : changeType.replace(/_/g, ' ').toLowerCase(),
    HAULER_FILTERS.find((h) => h.key === haulers)?.label.toLowerCase() ?? '',
  ].join(' · ');

  const selectStyle = { ...fieldStyle, minHeight: '34px', maxWidth: '180px', fontSize: '13px' };

  return (
    <section style={{ marginTop: '28px' }}>
      <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>
        Change Review Queue
        {pendingCount !== undefined ? ` · ${formatNumber(pendingCount)} pending` : ''}
      </h3>
      <p style={{ ...subText, marginBottom: '12px', textTransform: 'none' }}>
        State-agency operator/well changes detected by sync. Nothing reaches a hauler until you
        approve.
      </p>

      <div
        style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '12px',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={(): void => {
              setTab(t.key);
              setOpenId(null);
            }}
            style={{
              fontSize: '13px',
              padding: '6px 12px',
              borderRadius: 'var(--radius-pill)',
              border: '1px solid var(--color-border-secondary)',
              cursor: 'pointer',
              backgroundColor: tab === t.key ? 'var(--color-accent-blue)' : 'transparent',
              color: tab === t.key ? 'var(--color-on-accent)' : 'var(--color-text-secondary)',
            }}
          >
            {t.label}
          </button>
        ))}
        <select
          value={agency}
          onChange={(e): void => setAgency(e.target.value)}
          style={selectStyle}
          aria-label="Agency"
        >
          <option value="">All agencies</option>
          {AGENCIES.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <select
          value={changeType}
          onChange={(e): void => setChangeType(e.target.value)}
          style={selectStyle}
          aria-label="Change type"
        >
          <option value="">All change types</option>
          {CHANGE_TYPES.map((c) => (
            <option key={c} value={c}>
              {c.replace(/_/g, ' ').toLowerCase()}
            </option>
          ))}
        </select>
        <select
          value={haulers}
          onChange={(e): void => setHaulers(e.target.value as HaulerFilter)}
          style={selectStyle}
          aria-label="Haulers"
        >
          {HAULER_FILTERS.map((h) => (
            <option key={h.key} value={h.key}>
              {h.label}
            </option>
          ))}
        </select>
        {tab === 'PENDING' && plan.ids.length > 0 ? (
          <Button
            type="button"
            onClick={(): void => {
              setBulkResult(null);
              setBulkPlan(plan);
            }}
            style={{ backgroundColor: 'var(--color-accent-blue)', color: 'var(--color-on-accent)' }}
          >
            Approve all with 0 haulers ({plan.ids.length})
          </Button>
        ) : null}
      </div>

      {bulkResult !== null ? (
        <p style={{ ...subText, marginBottom: '12px', textTransform: 'none' }} role="status">
          {bulkResult}
        </p>
      ) : null}

      <BulkApproveModal
        plan={bulkPlan}
        filters={filtersInWords}
        onOpenChange={(open): void => {
          if (!open) setBulkPlan(null);
        }}
        onDone={setBulkResult}
      />

      {isLoading ? (
        <p style={subText}>Loading…</p>
      ) : changes.length === 0 ? (
        <p style={subText}>No changes in this view.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {changes.map((c) => (
            <React.Fragment key={c.id}>
              <button
                type="button"
                onClick={(): void => setOpenId(openId === c.id ? null : c.id)}
                style={{
                  textAlign: 'left',
                  backgroundColor: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border-primary)',
                  borderRadius: 'var(--radius-pill)',
                  padding: '12px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '16px',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontWeight: 600, fontSize: '14px' }}>
                    {c.wellName ?? c.display.summary}
                    {c.apiNumber !== null ? <span style={subText}> · {c.apiNumber}</span> : null}
                  </span>
                  <span style={subText}>
                    {c.stateAgency} · {c.display.summary}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                  <span style={{ ...subText, textTransform: 'none' }}>
                    {usageLabel(c)}
                    {c.affectedHaulerCount === 0 &&
                    c.reviewStatus === 'PENDING' &&
                    (bulkExclusion(c) === 'open_jobs' || bulkExclusion(c) === 'operator_links')
                      ? ' · skipped by bulk approve'
                      : ''}
                  </span>
                  <Pill
                    label={c.reviewStatus}
                    tone={c.reviewStatus === 'PENDING' ? 'warning' : 'muted'}
                  />
                </div>
              </button>
              {openId === c.id ? (
                <DetailBoundary key={c.id} onClose={(): void => setOpenId(null)}>
                  <DetailPanel id={c.id} onClose={(): void => setOpenId(null)} />
                </DetailBoundary>
              ) : null}
            </React.Fragment>
          ))}
        </div>
      )}
    </section>
  );
}
